import React, {
  useCallback, useEffect, useRef,
} from 'react';
import {
  Button as MuiButton, CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  inProgress, loaderHasFailed, loaderHasSucceeded, loaderHasSucceededData,
} from '@client/selectors/helpers/loaders';
import { useInstanceSelector } from '@client/hooks/redux.hooks';
import { trackAnalytics } from '@client/intercom';
import { AnalyticsEvents } from '@sda/interfaces/queue/analytics';
import { translateColor } from './common';
import type { ScopedButtonProps } from './interfaces';
import type { GenericMouseEvent } from '../types';

const useStyles = makeStyles({ name: 'SDAButton' })((theme) => ({
  button: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: 'calc(50% - 12px)',
    left: 'calc(50% - 12px)',
  },
  buttonProgressContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.mixins.alpha(theme.palette.grey[50], 0.6),
  },
  disabled: {
    pointerEvents: 'none',
  },
}));

const getLabelFromChildren = (children: React.ReactNode): string => {
  let label = '';

  React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      label += child;
    }
  });

  return label;
};

export const Button = <C extends React.ElementType = typeof MuiButton>({
  disabled = false, scope, id, children,
  onClick, onError, onSuccess, form,
  className, color = 'primary', CircularProgressProps = {}, ButtonProps = {},
  component: Component = MuiButton,
  ...restProps
}: ScopedButtonProps<C> & React.ComponentPropsWithRef<C>) => {
  const { classes, cx } = useStyles();
  const wasInProgress = useRef(null);

  const isInProgress = useInstanceSelector(inProgress, scope);
  const hasFailed = useInstanceSelector(loaderHasFailed, scope);
  const hasSucceeded = useInstanceSelector(loaderHasSucceeded, scope);
  const successData = useInstanceSelector(loaderHasSucceededData, scope);

  useEffect(() => {
    if (wasInProgress.current && !isInProgress && hasSucceeded) {
      if (onSuccess) {
        onSuccess(successData);
      }
    }

    if (wasInProgress.current && !isInProgress && hasFailed && onError) {
      onError(hasFailed);
    }

    wasInProgress.current = isInProgress;
  }, [isInProgress, onError, onSuccess, hasFailed, successData, hasSucceeded]);

  const handleClick = useCallback((e: GenericMouseEvent) => {
    if (onClick && !disabled && !isInProgress) {
      onClick(e);
    }

    const selector = id || scope;
    const name = getLabelFromChildren(children);

    trackAnalytics(AnalyticsEvents.CLICKED_BUTTON, { selector, name });
  }, [children, id, scope, disabled, isInProgress, onClick]);
  const disabledProp = !!(disabled || isInProgress);

  const baseButtonProps = Component !== MuiButton ? {} : {
    variant: 'contained',
    fullWidth: false,
  };

  return (
    <Component
      className={cx(className, disabledProp && classes.disabled, classes.button)}
      disabled={disabledProp}
      id={id || scope}
      onClick={handleClick}
      color={color}
      size="medium"
      type="submit"
      {...baseButtonProps}
      {...restProps}
      {...ButtonProps}
    >
      {children}

      {isInProgress && (
        <div className={classes.buttonProgressContainer}>
          <CircularProgress
            size={24}
            className={classes.buttonProgress}
            color={translateColor(color)}
            {...CircularProgressProps || {}}
          />
        </div>
      )}
    </Component>
  );
};
